<script>
import Layout from "@/router/layouts/auth";
import { api } from "@/state/services";
import { required, minLength, sameAs } from "vuelidate/lib/validators";
import { checkCpf } from "@/state/validations/register";
import Languages from "@/components/widgets/languages";
import Password from "@/components/widgets/input-password";

export default {
  locales: {
    pt: {
      'CPF': 'CPF',
      'CPF is required.': 'CPF é obrigatório.',
      'This value should be a valid CPF.': 'Informe um CPF válido.',
      'Enter your CPF to receive a 6-digit code by e-mail.': 'Informe seu CPF para receber por e-mail um código para recuperar sua senha.',
      'We sent a 6-digit code to': 'Enviamos um código de 6 dígitos para',
      'It is valid for 15 minutes.': 'Ele é válido por 15 minutos.',
      'Code': 'Código',
      'Enter the 6-digit code.': 'Informe o código de 6 dígitos.',
      'Send code': 'Enviar código',
      'Validate code': 'Validar código',
      'Resend code': 'Reenviar código',
      'Resend code in': 'Reenviar código em',
      'New code sent.': 'Novo código enviado.',
      'New password': 'Nova senha',
      'Confirm password': 'Confirmar senha',
      'Save new password': 'Salvar nova senha',
      'Go to login': 'Ir para o login',
      'Connection error. Try again.': 'Erro de conexão. Tente novamente.',
      'Remember It?': 'Lembrou-se?',
      'Sign In here': 'Entrar',
    },
    es: {
      'CPF': 'CPF',
      'CPF is required.': 'CPF es requerido.',
      'This value should be a valid CPF.': 'Ingrese un CPF válido.',
      'Enter your CPF to receive a 6-digit code by e-mail.': 'Ingrese su CPF para recibir por correo un código para recuperar su contraseña.',
      'We sent a 6-digit code to': 'Enviamos un código de 6 dígitos a',
      'It is valid for 15 minutes.': 'Es válido por 15 minutos.',
      'Code': 'Código',
      'Enter the 6-digit code.': 'Ingrese el código de 6 dígitos.',
      'Send code': 'Enviar código',
      'Validate code': 'Validar código',
      'Resend code': 'Reenviar código',
      'Resend code in': 'Reenviar código en',
      'New code sent.': 'Nuevo código enviado.',
      'New password': 'Nueva contraseña',
      'Confirm password': 'Confirmar contraseña',
      'Save new password': 'Guardar nueva contraseña',
      'Go to login': 'Ir al inicio de sesión',
      'Connection error. Try again.': 'Error de conexión. Inténtelo de nuevo.',
      'Remember It?': '¿Recuerdalo?',
      'Sign In here': 'Identifícate aquí',
    }
  },
  components: {
    Layout,
    Languages,
    Password
  },
  data() {
    return {
      // Passos: cpf -> code -> password -> done
      step: "cpf",
      loading: false,
      cpf: "",
      code: "",
      newest: "",
      confirm: "",
      maskedEmail: "",
      countdown: 0,
      timer: null,
      alert: {
        type: "",
        message: ""
      }
    };
  },
  validations: {
    cpf: {
      required,
      validCpf: (value) => checkCpf(value)
    },
    code: { required, minLength: minLength(6) },
    newest: { required, minLength: minLength(6) },
    confirm: { required, minLength: minLength(6), sameAs: sameAs("newest") }
  },
  beforeDestroy() {
    clearInterval(this.timer);
  },
  methods: {
    // Inicia a contagem regressiva para liberar o reenvio do código
    startCountdown(seconds) {
      clearInterval(this.timer);
      this.countdown = seconds;
      this.timer = setInterval(() => {
        this.countdown -= 1;
        if (this.countdown <= 0) {
          clearInterval(this.timer);
        }
      }, 1000);
    },
    setError(message) {
      this.alert.type = "alert-danger";
      this.alert.message = message;
    },
    setSuccess(message) {
      this.alert.type = "alert-success";
      this.alert.message = message;
    },
    clearAlert() {
      this.alert.type = "";
      this.alert.message = "";
    },
    // Mantém apenas dígitos no campo do código
    formatCode() {
      this.code = this.code.replace(/\D/g, "").slice(0, 6);
    },
    // Solicita (ou reenvia) o código de recuperação
    recoverSubmit() {
      this.clearAlert();
      this.$v.cpf.$touch();
      if (this.$v.cpf.$invalid) {
        return;
      }

      this.loading = true;
      api
        .post("password/recover", { personNumber: this.cpf })
        .then((response) => {
          const data = response.data;
          if (data.status == "success" || data.status == "wait") {
            const resend = this.step == "code";
            this.maskedEmail = data.email;
            this.startCountdown(data.wait);
            this.step = "code";
            if (resend) {
              this.code = "";
              this.$v.code.$reset();
              this.setSuccess(data.status == "success" ? this.t("New code sent.") : data.message);
            }
          } else {
            this.setError(data.message);
          }
        })
        .catch(() => {
          this.setError(this.t("Connection error. Try again."));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    // Valida o código informado
    validateSubmit() {
      this.clearAlert();
      this.$v.code.$touch();
      if (this.$v.code.$invalid) {
        return;
      }

      this.loading = true;
      api
        .post("password/validate", { personNumber: this.cpf, code: this.code })
        .then((response) => {
          if (response.data.status == "success") {
            this.step = "password";
          } else {
            this.setError(response.data.message);
          }
        })
        .catch(() => {
          this.setError(this.t("Connection error. Try again."));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    // Envia a nova senha junto com o código (revalidado no servidor)
    changeSubmit() {
      this.clearAlert();
      this.$v.newest.$touch();
      this.$v.confirm.$touch();
      if (this.$v.newest.$invalid || this.$v.confirm.$invalid) {
        return;
      }

      this.loading = true;
      api
        .post("password/change", {
          personNumber: this.cpf,
          code: this.code,
          new: this.newest,
          confirm: this.confirm
        })
        .then((response) => {
          if (response.data.status == "success") {
            this.setSuccess(response.data.message);
            this.step = "done";
          } else {
            this.setError(response.data.message);
          }
        })
        .catch(() => {
          this.setError(this.t("Connection error. Try again."));
        })
        .finally(() => {
          this.loading = false;
        });
    }
  }
};
</script>

<template>
  <Layout>
    <div id="auth" class="d-flex">
      <div class="login-content">
        <div class="pt-5 pb-2 text-center">
          <router-link class="align-middle" tag="a" to="/">
            <img class="w-100" style="max-width: 250px" src="@/assets/images/logo.png" />
          </router-link>
        </div>
        <div class="py-5">
          <div v-if="alert.message" :class="'alert ' + alert.type">
            {{ alert.message }}
          </div>

          <!-- Passo 4: senha alterada -->
          <div v-if="step == 'done'">
            <router-link tag="a" to="/login" class="btn btn-default btn-lg btn-block">{{ t("Go to login") }}</router-link>
          </div>

          <!-- Passo 1: informar CPF -->
          <b-form v-else-if="step == 'cpf'" @submit.prevent="recoverSubmit">
            <p>{{ t("Enter your CPF to receive a 6-digit code by e-mail.") }}</p>
            <b-form-group id="cpf" :label="t('CPF')" label-for="cpf-input">
              <b-form-input id="cpf-input" v-model="cpf" v-mask="'###.###.###-##'" inputmode="numeric"
                placeholder="000.000.000-00" :class="{ 'is-invalid': $v.cpf.$error }"></b-form-input>
              <div v-if="$v.cpf.$error" class="invalid-feedback">
                <span v-if="!$v.cpf.required">{{ t("CPF is required.") }}</span>
                <span v-else-if="!$v.cpf.validCpf">{{ t("This value should be a valid CPF.") }}</span>
              </div>
            </b-form-group>
            <div class="mt-4">
              <b-button :disabled="loading == true" type="submit" variant="default" class="btn-lg btn-block">
                {{ t("Send code") }}
                <b-spinner v-if="loading" small class="ml-2 align-middle" variant="white" role="status"></b-spinner>
              </b-button>
            </div>
          </b-form>

          <!-- Passo 2: validar código -->
          <b-form v-else-if="step == 'code'" @submit.prevent="validateSubmit">
            <p>{{ t("We sent a 6-digit code to") }} <strong>{{ maskedEmail }}</strong>. {{ t("It is valid for 15 minutes.") }}</p>
            <b-form-group id="code" :label="t('Code')" label-for="code-input">
              <b-form-input id="code-input" v-model="code" @input="formatCode" inputmode="numeric" maxlength="6"
                autocomplete="one-time-code" placeholder="000000" class="text-center"
                :class="{ 'is-invalid': $v.code.$error }"></b-form-input>
              <div v-if="$v.code.$error" class="invalid-feedback">
                <span>{{ t("Enter the 6-digit code.") }}</span>
              </div>
            </b-form-group>
            <div class="mt-4">
              <b-button :disabled="loading == true" type="submit" variant="default" class="btn-lg btn-block">
                {{ t("Validate code") }}
                <b-spinner v-if="loading" small class="ml-2 align-middle" variant="white" role="status"></b-spinner>
              </b-button>
            </div>
            <div class="mt-3 text-center">
              <b-button :disabled="countdown > 0 || loading" variant="link" @click="recoverSubmit">
                <span v-if="countdown > 0">{{ t("Resend code in") }} {{ countdown }}s</span>
                <span v-else>{{ t("Resend code") }}</span>
              </b-button>
            </div>
          </b-form>

          <!-- Passo 3: nova senha -->
          <b-form v-else @submit.prevent="changeSubmit">
            <b-form-group id="newest" :label="t('New password')">
              <Password v-model="newest" :v="$v.newest"></Password>
            </b-form-group>
            <b-form-group id="confirm" :label="t('Confirm password')">
              <Password v-model="confirm" :v="$v.confirm"></Password>
            </b-form-group>
            <div class="mt-4">
              <b-button :disabled="loading == true" type="submit" variant="default" class="btn-lg btn-block">
                {{ t("Save new password") }}
                <b-spinner v-if="loading" small class="ml-2 align-middle" variant="white" role="status"></b-spinner>
              </b-button>
            </div>
          </b-form>

          <div class="mt-4">
            <p class="text-dark">
              {{ t("Remember It?") }}
              <router-link tag="a" to="/login" class="font-weight-medium text-muted">{{ t("Sign In here")
                }}</router-link>
            </p>
            <Languages class="d-none" type="dropup"></Languages>
          </div>
        </div>
      </div>
      <div class="flex-fill login-image d-none d-lg-block"></div>
    </div>
  </Layout>
</template>
